// components/layout/BookCallPrompt.jsx
//
// This is a demo deployment, so nudge people to book a call while they explore. A card pops up
// bottom-right after a short delay; dismissing it leaves a small "Book a call" pill so the offer
// stays one click away, and the card pops up again 2 minutes after each dismissal.

import { useEffect, useState } from 'react';
import { PhoneIcon, XMarkIcon } from '@heroicons/react/24/outline';

const BOOKING_URL = 'https://calendly.com/scale-strategy/scale-demo';
const FIRST_SHOW_DELAY_MS = 20 * 1000;
const RESHOW_DELAY_MS = 2 * 60 * 1000;

export function BookCallPrompt() {
  const [open, setOpen] = useState(false);
  const [dismissedOnce, setDismissedOnce] = useState(false);

  // First appearance after FIRST_SHOW_DELAY_MS
  useEffect(() => {
    const t = setTimeout(() => setOpen(true), FIRST_SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // After a dismissal, bring the card back RESHOW_DELAY_MS later
  useEffect(() => {
    if (!dismissedOnce || open) return undefined;
    const t = setTimeout(() => setOpen(true), RESHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [dismissedOnce, open]);

  const dismiss = () => {
    setOpen(false);
    setDismissedOnce(true);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 print:hidden">
      {open && (
        <div
          role="dialog"
          aria-label="Book a call"
          className="relative w-72 max-w-[calc(100vw-2rem)] rounded-2xl border border-grey-200 bg-white p-4 shadow-2xl animate-slide-up"
        >
          <button
            type="button"
            onClick={dismiss}
            className="absolute right-2 top-2 rounded-lg p-1 text-grey-500 transition-colors hover:bg-grey-100"
            aria-label="Dismiss"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <p className="pr-6 text-xs font-semibold uppercase tracking-wider text-brand-600">
            You&rsquo;re viewing a demo
          </p>
          <p className="mt-1 text-base font-semibold text-darkblue-500">
            Want a PULSE on your data?
          </p>
          <p className="mt-1 text-sm text-grey-800">
            Book a quick call and we&rsquo;ll show you this running on your own numbers.
          </p>

          <img
            src="/qr-booking.png"
            alt="Book a call QR code"
            className="mx-auto mt-3 h-28 w-28 rounded border border-grey-200 bg-white p-1.5"
          />

          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block w-full rounded-lg bg-brand-600 px-4 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            Book Your Call Today
          </a>
        </div>
      )}

      {!open && dismissedOnce && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 rounded-full bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-lg transition-colors hover:bg-brand-700"
        >
          <PhoneIcon className="h-4 w-4" />
          Book a call
        </button>
      )}
    </div>
  );
}

export default BookCallPrompt;
